import React, {useContext, useMemo} from "react";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Paper from "@mui/material/Paper";
import Badge from "@mui/material/Badge";
// Icons
import FavoriteIcon from "@mui/icons-material/Favorite";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import {useNavigate, useLocation} from "react-router-dom";
import CartItemsContext from "../../../../context/cartItems/CartItemsContext";
import LikedItemsContext from "../../../../context/likedItems/LikedItemsContext";

export default function FixedBottomNavigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const ref = React.useRef(null);

  const cartItemsContext = useContext(CartItemsContext);
  const likedItemsContext = useContext(LikedItemsContext);

  const cartItemsArray = cartItemsContext?.state;
  const totalCartItems = useMemo(() => {
    if (!cartItemsArray || !Array.isArray(cartItemsArray)) return 0;
    return cartItemsArray.reduce((n, {quantity}) => n + (quantity || 1), 0);
  }, [cartItemsArray]);

  const likedItemsArray = likedItemsContext?.state;
  const totalLikedItems = useMemo(() => {
    if (!likedItemsArray || !Array.isArray(likedItemsArray)) return 0;
    return likedItemsArray.length;
  }, [likedItemsArray]);

  // Derive active tab from current pathname
  const activeValue = useMemo(() => {
    const path = location.pathname;
    if (path === "/") return "home";
    if (path.startsWith("/collections")) return "categories";
    if (path === "/myWishlist") return "wishlist";
    if (path.startsWith("/userOrders") || path.startsWith("/orders")) return "orders";
    if (path === "/cart") return "cart";
    return false;
  }, [location.pathname]);

  const handleChange = (event, newValue) => {
    if (ref.current?.ownerDocument?.body) {
      ref.current.ownerDocument.body.scrollTop = 0;
    }
    switch (newValue) {
      case "home":
        navigate("/");
        break;
      case "categories":
        navigate("/collections");
        break;
      case "wishlist":
        navigate("/myWishlist");
        break;
      case "orders":
        navigate("/userOrders");
        break;
      case "cart":
        navigate("/cart");
        break;
      default:
        break;
    }
  };

  return (
    <Box sx={{p: 1}} ref={ref}>
      <CssBaseline />

      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 10,
        }}
        elevation={3}
      >
        <BottomNavigation
          showLabels
          value={activeValue}
          onChange={handleChange}
        >
          <BottomNavigationAction
            sx={{minWidth: "70px"}}
            label="Home"
            value="home"
            icon={<HomeOutlinedIcon />}
          />
          <BottomNavigationAction
            sx={{minWidth: "70px"}}
            label="Categories"
            value="categories"
            icon={<CategoryOutlinedIcon />}
          />
          <BottomNavigationAction
            sx={{minWidth: "70px"}}
            label="Wishlist"
            value="wishlist"
            icon={
              <Badge
                badgeContent={totalLikedItems}
                max={99}
                color="error"
                className="wishlist-badge"
              >
                <FavoriteIcon sx={totalLikedItems > 0 ? {color: "#e11d48"} : undefined} />
              </Badge>
            }
          />
          <BottomNavigationAction
            sx={{minWidth: "70px"}}
            label="Orders"
            value="orders"
            icon={<FormatListBulletedOutlinedIcon />}
          />
          <BottomNavigationAction
            sx={{minWidth: "70px"}}
            label="Cart"
            value="cart"
            icon={
              <Badge
                badgeContent={totalCartItems}
                max={99}
                color="primary"
                className="cart-badge"
              >
                <ShoppingCartOutlinedIcon />
              </Badge>
            }
          />
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
